import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import apiClient from '../../api/client';
import WorkbenchTab, { WorkbenchIcon } from '../../components/chemistry/compound-tabs/WorkbenchTab';
import TargetsTab from '../../components/chemistry/compound-tabs/TargetsTab';
import PathwaysTab from '../../components/chemistry/compound-tabs/PathwaysTab';
import ReactionsTab from '../../components/chemistry/compound-tabs/ReactionsTab';
import BioactivityTab from '../../components/chemistry/compound-tabs/BioactivityTab';
import PatentsTab from '../../components/chemistry/compound-tabs/PatentsTab';
import RegistrationTab from '../../components/chemistry/compound-tabs/RegistrationTab';
import TargetUniverseTab from '../../components/chemistry/compound-tabs/TargetUniverseTab';
import ADMETTab from '../../components/chemistry/compound-tabs/ADMETTab';
import SARTab from '../../components/chemistry/compound-tabs/SARTab';
import MetabolismTab from '../../components/chemistry/compound-tabs/MetabolismTab';
import DesignTab from '../../components/chemistry/compound-tabs/DesignTab';
import { StructureEditorModal } from '../../components/chemistry/StructureEditor';
import StructureImage from '../../components/chemistry/StructureImage';
import type { DetailTab } from '../../components/chemistry/CompoundDetailPanel';

interface MoleculeData {
  id: number;
  file_id: number;
  smiles: string | null;
  mw: number | null;
  logp: number | null;
  tpsa: number | null;
  hbd: number | null;
  hba: number | null;
  rotatable_bonds: number | null;
  ring_count: number | null;
  properties: Record<string, any>;
  molfile?: string | null;
}

const DATA_TABS: { id: DetailTab; label: string; icon?: React.ReactNode }[] = [
  { id: 'registration', label: 'Reg' },
  { id: 'workbench', label: 'Toolbox', icon: <WorkbenchIcon size={14} /> },
  { id: 'targets', label: 'Targets' },
  { id: 'pathways', label: 'Pathways' },
  { id: 'reactions', label: 'Reactions' },
  { id: 'bioactivity', label: 'Bio' },
  { id: 'patents', label: 'Patents' },
];

const ANALYSIS_TABS: { id: DetailTab; label: string; icon?: React.ReactNode }[] = [
  { id: 'admet', label: 'ADMET' },
  { id: 'sar', label: 'SAR' },
  { id: 'metabolism', label: 'Metab' },
  { id: 'target-universe', label: 'Universe' },
  { id: 'design', label: 'Design' },
];

const TABS = [...DATA_TABS, ...ANALYSIS_TABS];

const MoleculeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [molecule, setMolecule] = useState<MoleculeData | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('workbench');
  const [editorOpen, setEditorOpen] = useState(false);

  const loadMoleculeData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const moleculeId = parseInt(id, 10);
      const moleculeRes = await chemistryApi.getMolecule(moleculeId);
      setMolecule(moleculeRes);

      // Fetch SVG for structure display
      if (moleculeRes.smiles) {
        try {
          const response = await apiClient.post('/v2/indigo/render', {
            structure: { value: moleculeRes.smiles, type: 'RAW' },
            width: '400px',
            height: '400px',
          });
          const svg = typeof response.data === 'string' ? response.data : response.data.svg || response.data;
          setSvgContent(svg);
        } catch {
          setSvgContent(null);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load molecule data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMoleculeData();
  }, [loadMoleculeData]);

  // Set document title for popout windows
  useEffect(() => {
    if (molecule) {
      const props = molecule.properties || {};
      const name = props.Name || props.name || props.Molecule || props.Compound || `Molecule #${molecule.id}`;
      document.title = `${name} — Adroit`;
    }
    return () => { document.title = 'Adroit'; };
  }, [molecule]);

  const getName = (): string => {
    if (!molecule) return '';
    const props = molecule.properties || {};
    for (const key of ['Name', 'name', 'Molecule', 'Compound', 'Title', 'ID']) {
      if (props[key] && String(props[key]).trim()) return String(props[key]).trim();
    }
    const smiles = molecule.smiles || '';
    return smiles.length > 30 ? `${smiles.substring(0, 30)}...` : smiles;
  };

  const formatProperty = (value: number | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined) return '-';
    return typeof value === 'number' ? value.toFixed(decimals) : value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error || !molecule) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Molecule not found'}</p>
          <button
            onClick={() => navigate('/chemistry/projects')}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const mw = molecule.mw ?? molecule.properties?.MW;

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--content-bg)' }}>
      {/* Compact header with structure + info */}
      <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
        <div className="flex items-start gap-4 px-4 pt-3 pb-2">
          {/* Structure thumbnail */}
          <div
            className="flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center"
            style={{ width: 200, height: 200, backgroundColor: 'var(--content-bg)' }}
          >
            {svgContent ? (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer transition-colors"
                style={{ backgroundColor: 'var(--content-bg)' }}
                dangerouslySetInnerHTML={{ __html: svgContent }}
                onDoubleClick={() => setEditorOpen(true)}
                title="Double-click to edit in Ketcher"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--content-bg)'; }}
              />
            ) : (
              <svg className="w-8 h-8" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }} title={getName()}>
                {getName()}
              </h2>
            </div>

            {/* SMILES */}
            {molecule.smiles && (
              <div
                className="mb-1 cursor-pointer"
                title={`Click to copy: ${molecule.smiles}`}
                onClick={() => navigator.clipboard.writeText(molecule.smiles!)}
              >
                <StructureImage smiles={molecule.smiles} moleculeId={molecule.id ? Number(molecule.id) : undefined} size={80} />
              </div>
            )}

            {/* Quick stats */}
            <div className="flex items-center gap-3 flex-wrap">
              {mw != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  MW <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{formatProperty(mw, 1)}</span>
                </span>
              )}
              {molecule.logp != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  LogP <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{formatProperty(molecule.logp)}</span>
                </span>
              )}
              {molecule.tpsa != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  TPSA <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{formatProperty(molecule.tpsa, 1)}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar — two rows */}
        <div className="px-2">
          <div className="flex gap-0.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {DATA_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-3 py-1.5 text-xs whitespace-nowrap transition-colors flex items-center gap-1"
                style={{
                  color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
                  backgroundColor: activeTab === tab.id ? 'var(--hover-bg)' : 'transparent',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                }}
              >
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-0.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {ANALYSIS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-3 py-1.5 text-xs whitespace-nowrap transition-colors flex items-center gap-1"
                style={{
                  color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
                  backgroundColor: activeTab === tab.id ? 'var(--hover-bg)' : 'transparent',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                }}
              >
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content — same components as CompoundDetailPanel */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'workbench' && molecule.smiles && <WorkbenchTab smiles={molecule.smiles} moleculeId={molecule.id} />}
        {activeTab === 'target-universe' && molecule.smiles && <TargetUniverseTab smiles={molecule.smiles} moleculeId={molecule.id} />}
        {activeTab === 'targets' && molecule.smiles && <TargetsTab smiles={molecule.smiles} moleculeId={molecule.id} />}
        {activeTab === 'pathways' && molecule.smiles && <PathwaysTab smiles={molecule.smiles} />}
        {activeTab === 'reactions' && molecule.smiles && <ReactionsTab smiles={molecule.smiles} />}
        {activeTab === 'bioactivity' && <BioactivityTab moleculeId={molecule.id} />}
        {activeTab === 'patents' && molecule.smiles && <PatentsTab smiles={molecule.smiles} />}
        {activeTab === 'registration' && molecule.smiles && (
          <RegistrationTab smiles={molecule.smiles} molfile={molecule.molfile || undefined} />
        )}
        {activeTab === 'admet' && molecule.smiles && <ADMETTab smiles={molecule.smiles} />}
        {activeTab === 'sar' && molecule.smiles && <SARTab smiles={molecule.smiles} />}
        {activeTab === 'metabolism' && molecule.smiles && <MetabolismTab smiles={molecule.smiles} />}
        {activeTab === 'design' && molecule.smiles && <DesignTab smiles={molecule.smiles} moleculeId={molecule.id} />}
        {!molecule.smiles && activeTab !== 'bioactivity' && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No SMILES available for this molecule</p>
          </div>
        )}
      </div>

      <StructureEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        initialStructure={molecule.molfile || molecule.smiles || ''}
        title={getName() || 'View Structure'}
      />
    </div>
  );
};

export default MoleculeDetailPage;
